/**
 * Stat account mapping — the revenue modal's "By Stat Account" mapping view.
 *
 * One table: each Occupied Rooms stat account (the segments stat-account
 * mode forecasts), the reporting category the room-stat mapping gives it,
 * and the revenue account its RN × ADR routes to. Users add segments here
 * directly — any stat-typed COA account, including ones already carried in
 * the room-stat mapping under another category — instead of round-tripping
 * through Revenue map → Room stats.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2, Plus, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { getStatMappingOptions, addStatAccountMapping, removeStatAccountMapping } from '../../api';
import type { StatAccountMapping, StatMappingCandidate, RevenueContextAccount } from '../../api';

interface StatAccountMappingPanelProps {
  orgId: number;
  locationId: number;
  statAccounts: StatAccountMapping[];
  /** Department revenue accounts — the link targets. */
  accounts: RevenueContextAccount[];
  isReadOnly: boolean;
  onRevenueAccountChange: (statAccountId: number, revenueChartOfAccountId: number | null) => void | Promise<void>;
  /** Segments were added/removed — reload statAccounts. */
  onMappingsChanged: () => void | Promise<void>;
}

const OCCUPIED = 'Occupied Rooms';

const CATEGORY_HINT: Record<string, string> = {
  'Occupied Rooms': 'Counts toward occupied room nights (Occ %, ADR, RevPAR, $POR) and forecasts as a segment',
  'Available Rooms': 'Counts toward available room nights (Occ %, RevPAR, $PAR)',
};

export function StatAccountMappingPanel({
  orgId,
  locationId,
  statAccounts,
  accounts,
  isReadOnly,
  onRevenueAccountChange,
  onMappingsChanged,
}: StatAccountMappingPanelProps) {
  const [candidates, setCandidates] = useState<StatMappingCandidate[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [addCoaId, setAddCoaId] = useState<number | ''>('');
  const [addRevenueId, setAddRevenueId] = useState<number | ''>('');
  const [busy, setBusy] = useState<'add' | number | null>(null);

  const loadOptions = useCallback(async () => {
    try {
      setLoadError(null);
      const opts = await getStatMappingOptions({ orgId, locationId });
      setCandidates(opts.candidates);
    } catch (err) {
      console.error('[StatAccountMappingPanel] options failed', err);
      setLoadError('Could not load stat accounts');
    }
  }, [orgId, locationId]);
  useEffect(() => { void loadOptions(); }, [loadOptions]);

  const revenueById = useMemo(() => new Map(accounts.map((a) => [a.chartOfAccountId, a])), [accounts]);
  const categoriesByCoa = useMemo(() => {
    const m = new Map<number, StatMappingCandidate>();
    (candidates || []).forEach((c) => m.set(c.chartOfAccountId, c));
    return m;
  }, [candidates]);

  const mappedCoaIds = useMemo(() => new Set(statAccounts.map((s) => s.chartOfAccountId)), [statAccounts]);
  const addable = useMemo(() => (candidates || []).filter((c) => !mappedCoaIds.has(c.chartOfAccountId)), [candidates, mappedCoaIds]);
  const inRoomStats = addable.filter((c) => c.categories.length > 0);
  const unmapped = addable.filter((c) => c.categories.length === 0);

  // Default the revenue link for a picked stat account to its name twin.
  const suggestRevenue = (coaId: number | ''): number | '' => {
    if (coaId === '') return '';
    const c = categoriesByCoa.get(coaId);
    if (!c) return '';
    const norm = (s: string) => s.trim().toLowerCase().replace(/\s*(#|stats?|statistics|room nights|rn)\s*$/i, '').replace(/\s*[-–—:]\s*$/, '').trim();
    const key = norm(c.accountName);
    const matches = accounts.filter((a) => norm(a.accountName) === key);
    if (matches.length === 1) return matches[0].chartOfAccountId;
    const num = c.accountNumber.replace(/\s+/g, '');
    const byNumber = accounts.filter((a) => {
      const n = (a.accountNumber || '').replace(/\s+/g, '');
      return n && n.length < num.length && num.endsWith(n);
    });
    return byNumber.length === 1 ? byNumber[0].chartOfAccountId : '';
  };

  const handleAdd = async () => {
    if (addCoaId === '' || busy) return;
    setBusy('add');
    try {
      const res = await addStatAccountMapping({
        orgId,
        locationId,
        chartOfAccountId: addCoaId,
        revenueChartOfAccountId: addRevenueId === '' ? null : addRevenueId,
      });
      const c = categoriesByCoa.get(addCoaId);
      toast.success(`${c?.accountName || 'Stat account'} added as an Occupied Rooms segment${res.mappedTypeCreated ? ' (created the Occupied Rooms category for this property)' : ''}`);
      setAddCoaId('');
      setAddRevenueId('');
      await Promise.all([onMappingsChanged(), loadOptions()]);
    } catch (err) {
      console.error('[StatAccountMappingPanel] add failed', err);
      const message = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      toast.error(message || 'Failed to add stat account');
    } finally {
      setBusy(null);
    }
  };

  const handleRemove = async (sa: StatAccountMapping) => {
    if (busy) return;
    setBusy(sa.id);
    try {
      await removeStatAccountMapping({ orgId, locationId, mappingId: sa.id });
      toast.success(`${sa.accountName} removed from Occupied Rooms`);
      await Promise.all([onMappingsChanged(), loadOptions()]);
    } catch (err) {
      console.error('[StatAccountMappingPanel] remove failed', err);
      toast.error('Failed to remove stat account');
    } finally {
      setBusy(null);
    }
  };

  const allLinked = statAccounts.length > 0 && statAccounts.every((sa) => sa.revenueChartOfAccountId);

  const revenueSelect = (value: number | '', onChange: (v: number | '') => void, opts: { amber?: boolean; placeholder?: string; disabled?: boolean } = {}) => (
    <select
      value={value}
      disabled={opts.disabled}
      onChange={(e) => onChange(e.target.value ? parseInt(e.target.value, 10) : '')}
      className={`w-full text-xs border rounded px-2 py-1 text-slate-700 bg-white disabled:cursor-not-allowed disabled:bg-slate-50 ${opts.amber ? 'border-amber-300 bg-amber-50' : 'border-slate-200'}`}
    >
      <option value="">{opts.placeholder || '— assign revenue account —'}</option>
      {accounts.map((a) => (
        <option key={a.chartOfAccountId} value={a.chartOfAccountId}>
          {a.accountNumber} – {a.accountName}
        </option>
      ))}
    </select>
  );

  const categoryBadges = (coaId: number) => {
    const c = categoriesByCoa.get(coaId);
    const types = c ? Array.from(new Set(c.categories.map((x) => x.mappedType).filter(Boolean))) : [];
    if (!types.includes(OCCUPIED)) types.unshift(OCCUPIED);
    return (
      <div className="flex flex-wrap gap-1">
        {types.map((t) => (
          <span
            key={t}
            className={`text-[10px] font-semibold rounded px-1.5 py-0.5 border ${t === OCCUPIED ? 'bg-orange-50 text-orange-700 border-orange-200' : 'bg-slate-50 text-slate-600 border-slate-200'}`}
            title={CATEGORY_HINT[t] || `Reports under Statistics › Total ${t}`}
          >
            {t}
          </span>
        ))}
      </div>
    );
  };

  return (
    <div className="p-5">
      <div className="flex items-start justify-between mb-3 gap-3">
        <p className="text-xs text-slate-600 leading-relaxed">
          Each segment's room nights × ADR route <strong>directly</strong> to its revenue account on apply.
          Segments are the property's <strong>Occupied Rooms</strong> stat accounts — the same mapping reports use for Occ %, ADR and $POR.
          {statAccounts.length > 0 && !allLinked && <span className="text-amber-600"> Assign a revenue account to each segment below.</span>}
        </p>
        {allLinked && (
          <span className="flex-shrink-0 text-[10px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-2 py-1">
            All segments linked
          </span>
        )}
      </div>

      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
        <table className="w-full border-collapse text-xs">
          <thead>
            <tr className="bg-slate-50 border-b-2 border-slate-200">
              <th className="text-left px-3 py-2 text-[10px] text-slate-400 font-semibold uppercase w-[34%]">Stat account</th>
              <th className="text-left px-3 py-2 text-[10px] text-slate-400 font-semibold uppercase w-[24%]">Reporting category</th>
              <th className="text-left px-3 py-2 text-[10px] text-slate-400 font-semibold uppercase border-l border-slate-200">Revenue account</th>
              <th className="w-8" />
            </tr>
          </thead>
          <tbody>
            {statAccounts.length === 0 && (
              <tr>
                <td colSpan={4} className="px-3 py-4 text-center text-slate-400 italic">
                  No Occupied Rooms stat accounts yet — add the first segment below.
                </td>
              </tr>
            )}
            {statAccounts.map((sa) => {
              const linked = sa.revenueChartOfAccountId ? revenueById.get(sa.revenueChartOfAccountId) : undefined;
              return (
                <tr key={sa.id} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="px-3 py-2">
                    <div className="font-semibold text-slate-800">{sa.accountName}</div>
                    <div className="text-[10px] text-slate-400">
                      {sa.accountNumber}
                      {sa.operation === 'subtract' && <span className="ml-1 text-rose-500">· subtracts</span>}
                    </div>
                  </td>
                  <td className="px-3 py-2">{categoryBadges(sa.chartOfAccountId)}</td>
                  <td className="px-3 py-2 border-l border-slate-200">
                    {isReadOnly ? (
                      linked ? (
                        <span className="text-slate-700">{linked.accountNumber} – {linked.accountName}</span>
                      ) : sa.revenueAccountName ? (
                        <span className="text-slate-700">{sa.revenueAccountNumber} – {sa.revenueAccountName}</span>
                      ) : (
                        <span className="text-amber-600">unlinked</span>
                      )
                    ) : (
                      revenueSelect(sa.revenueChartOfAccountId ?? '', (v) => void onRevenueAccountChange(sa.id, v === '' ? null : v), { amber: !sa.revenueChartOfAccountId })
                    )}
                  </td>
                  <td className="px-1 py-2 text-center">
                    {!isReadOnly && (
                      <button
                        type="button"
                        onClick={() => void handleRemove(sa)}
                        disabled={busy !== null}
                        className="p-1 rounded text-slate-300 hover:text-red-500 hover:bg-red-50 disabled:opacity-40"
                        title="Remove from Occupied Rooms (the account stays in the chart; re-adding restores its link)"
                      >
                        {busy === sa.id ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}

            {!isReadOnly && (
              <tr className="bg-slate-50/60 border-t-2 border-slate-200">
                <td className="px-3 py-2" colSpan={2}>
                  {loadError ? (
                    <span className="text-rose-500">{loadError}</span>
                  ) : candidates === null ? (
                    <span className="inline-flex items-center gap-1 text-slate-400"><Loader2 size={11} className="animate-spin" />Loading stat accounts…</span>
                  ) : (
                    <select
                      value={addCoaId}
                      onChange={(e) => {
                        const v = e.target.value ? parseInt(e.target.value, 10) : '';
                        setAddCoaId(v);
                        setAddRevenueId(suggestRevenue(v));
                      }}
                      className="w-full text-xs border border-slate-200 rounded px-2 py-1 text-slate-700 bg-white"
                    >
                      <option value="">+ Add a stat account as an Occupied Rooms segment…</option>
                      {inRoomStats.length > 0 && (
                        <optgroup label="Already in the room-stat mapping (other category)">
                          {inRoomStats.map((c) => (
                            <option key={c.chartOfAccountId} value={c.chartOfAccountId}>
                              {c.accountNumber} – {c.accountName} · {Array.from(new Set(c.categories.map((x) => x.mappedType))).join(', ')}
                            </option>
                          ))}
                        </optgroup>
                      )}
                      {unmapped.length > 0 && (
                        <optgroup label="Stat accounts not yet mapped">
                          {unmapped.map((c) => (
                            <option key={c.chartOfAccountId} value={c.chartOfAccountId}>
                              {c.accountNumber} – {c.accountName}{c.accountType ? ` · ${c.accountType}` : ''}
                            </option>
                          ))}
                        </optgroup>
                      )}
                      {addable.length === 0 && <option disabled value="__none">No more stat-typed accounts to add</option>}
                    </select>
                  )}
                </td>
                <td className="px-3 py-2 border-l border-slate-200">
                  {revenueSelect(addRevenueId, setAddRevenueId, { disabled: addCoaId === '', placeholder: '— revenue account (optional now) —' })}
                </td>
                <td className="px-1 py-2 text-center">
                  <button
                    type="button"
                    onClick={() => void handleAdd()}
                    disabled={addCoaId === '' || busy !== null}
                    className="inline-flex items-center gap-1 rounded px-2 py-1 text-[11px] font-semibold text-white disabled:opacity-40"
                    style={{ background: 'var(--lodge-orange-500)' }}
                    title="Add segment"
                  >
                    {busy === 'add' ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />}
                    Add
                  </button>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <p className="mt-2 text-[10px] text-slate-400">
        Adding here writes the same room-stat mapping the Revenue map page edits; the account then reports under Statistics › Total Occupied Rooms.
      </p>
    </div>
  );
}
